import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { ExternalLink, Users } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import { useI18n } from "@/i18n/LanguageContext";
interface Partner {
  id: string;
  name: string;
  logo_url: string;
  website_url?: string;
  description?: string;
  order_index: number;
}
export const Partners = () => {
  const { t } = useI18n();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const {
          data,
          error
        } = await supabase.from('partners').select('*').eq('is_active', true).order('order_index', {
          ascending: true
        });
        if (error) throw error;
        setPartners(data || []);
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);
  if (loading) {
    return <section className="app-section bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="h-8 bg-muted animate-pulse rounded mb-4 max-w-xs mx-auto"></div>
            <div className="h-4 bg-muted animate-pulse rounded max-w-md mx-auto"></div>
          </div>
        </div>
      </section>;
  }
  if (partners.length === 0) {
    return null;
  }
  return <section className="app-section bg-background">
      <div className="app-container">
        <div className="text-center mb-12">
          <div className="mb-4">
            <Users className="h-12 w-12 mx-auto text-primary" aria-hidden="true" />
          </div>
          <h2 className="section-title">{t.partners.title}</h2>
          <p className="section-copy mx-auto">
            {t.partners.copy}
          </p>
        </div>

        <div className="relative">
            <Carousel opts={{
          align: "start",
          loop: true
        }} plugins={[Autoplay({
          delay: 3000
        })]} className="w-full">
            <CarouselContent className="-ml-2 md:-ml-4">
              {partners.map(partner => <CarouselItem key={partner.id} className="pl-2 md:pl-4 basis-full xs:basis-1/2 md:basis-1/3 lg:basis-1/4">
                  <Card className="app-card group h-full">
                    <CardContent className="p-6 flex flex-col items-center text-center h-full">
                      <div className="mb-4 w-full h-32 flex items-center justify-center rounded-md border border-border/60 p-4 overflow-hidden">
                        <img src={partner.logo_url} alt={partner.name} className="block w-full h-full object-contain object-center [filter:brightness(0)_saturate(100%)] dark:[filter:brightness(0)_saturate(100%)_invert(1)] transition-[transform,filter] duration-300 group-hover:scale-105" loading="lazy" decoding="async" sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw" />
                      </div>
                      <h3 className="font-semibold text-lg text-foreground mb-2 group-hover:text-primary transition-colors">
                        {partner.name}
                      </h3>
                      {partner.description && <p className="text-sm text-muted-foreground mb-4 flex-1">
                          {partner.description}
                        </p>}
                      {partner.website_url && <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="mt-auto inline-flex items-center gap-2 text-primary hover:text-highlight transition-colors font-medium">
                          <span>{t.partners.visit}</span>
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                        </a>}
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>;
};